import { defineStore } from 'pinia'
import { useGraphqlFetch } from '@/composables/craft-fetch'
import { fallbackDistance, useLocationSearchStore } from '~/store/location-search'
import { useCacheStore } from '~/store/cache'
import { useGlobalStore } from '~/store/global'

const hitsPerPage = 25

export const useDepartmentOverviewStore = defineStore('department-overview', () => {
  const locationSearchStore = useLocationSearchStore()
  const cacheStore = useCacheStore()
  const globalStore = useGlobalStore()
  const graphqlFetch = useGraphqlFetch()

  const store = {
    // Search context
    departmentType: ref<string | undefined>(undefined),
    departmentVisibility: ref<string | undefined>(undefined),
    distance: ref(fallbackDistance),
    location: ref(''),
    page: ref(1),

    // Location search results
    city: ref<string | undefined>(undefined),
    errors: ref<Array<Error>>([]),
    lat: ref<number | undefined>(undefined),
    lng: ref<number | undefined>(undefined),
    type: ref<string | undefined>(undefined),

    // Results
    departments: ref<Array<any>>([]),
    hits: ref<Array<string>>([]),
    totalHits: ref(0),
  }

  // Getters
  store.hitsPerPage = computed(() => hitsPerPage)
  store.urlParameters = computed(() => ({
    distance: store.distance.value === fallbackDistance ? undefined : store.distance.value,
    location: store.location.value ? store.location.value : undefined,
    page: store.page.value === 1 ? undefined : store.page.value,
  }))

  function initialize({
    departmentType,
    departmentVisibility,
    distance,
    location,
    page,
  }: {
    departmentType: string
    departmentVisibility: string
    distance: number
    location: string
    page: number
  }) {
    store.departmentType.value = departmentType
    store.departmentVisibility.value = departmentVisibility
    store.distance.value = distance || fallbackDistance
    store.location.value = location || ''
    store.page.value = Math.max(1, page || 1)
  }

  async function fetchDepartment() {
    const { city, errors, lat, lng, status, type } = await locationSearchStore.resolve({ distance: store.distance.value, location: store.location.value })
    setLocationSearchResults({ city, errors, lat, lng, type })
    if (status === 'error') {
      return
    }

    const key = `department-overview;departmentType:${store.departmentType.value};departmentVisibility:${store.departmentVisibility.value};lat:${lat};lng:${lng};distance:${store.distance.value}`
    const ttl = 1

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query findDepartments($departmentVisibility: [String], $departmentType: [String], $mapDistance: String, $orderBy: [String]) {
            elasticSearch(
              site: "default"
              section: "department"
              departmentVisibilityFacet: $departmentVisibility
              departmentTypeFacet: $departmentType
              orderBy: $orderBy
              mapDistance: $mapDistance
            ) {
              count
              hits (limit:1024) {
                slug
              }
            }
          }
        `,
        variables: {
          departmentVisibility: store.departmentVisibility.value,
          departmentType: store.departmentType.value,
          orderBy: status === 'ok' ? [`map POINT(${lng} ${lat}) ASC`] : ['title ASC'],
          mapDistance: status === 'ok' ? `POINT(${lng} ${lat}) ${store.distance.value}` : undefined,
        },
      })

      return {
        hits: elasticSearch.hits.map(({ slug }) => slug),
        totalHits: elasticSearch.count,
      }
    }

    // Fetch the hits
    const { hits, totalHits } = await cacheStore.fetch({ key, ttl, fetcher, fallback: { hits: [], totalHits: 0 } })
    store.hits.value = hits
    store.totalHits.value = totalHits

    // Slice all associated entries within the current page
    const slice = hits.slice((store.page.value - 1) * hitsPerPage, store.page.value * hitsPerPage)
    const departments = await Promise.all(slice.map((slug) => globalStore.fetch({ slug, site: undefined, push: false })))
    store.departments.value = departments.filter((department) => department)
    return departments
  }

  function setDistance(distance: number) {
    store.distance.value = distance || fallbackDistance
    fetchDepartment()
  }

  function setLocation(location: string) {
    store.location.value = location || ''
    fetchDepartment()
  }

  function setPage(page: number) {
    store.page.value = Math.max(1, page)
    fetchDepartment()
  }

  /** @internal */
  function setLocationSearchResults({ city, errors, lat, lng, type }: { city: string; errors: Array<Error>; lat: number; lng: number; type: string }) {
    store.city.value = city
    store.errors.value = errors
    store.lat.value = lat
    store.lng.value = lng
    store.type.value = type
  }

  return {
    ...store,
    initialize,
    fetch: fetchDepartment,
    setDistance,
    setLocation,
    setPage,
  }
})
